import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Button,
  Stack
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  CardGiftcard as CardGiftcardIcon,
  CheckCircle as CheckCircleIcon,
  AutoAwesome as AutoAwesomeIcon,
  AttachMoney,
  Schedule as ScheduleIcon,
  Mail as EmailIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useLocalization } from '../hooks/useLocalization';

const BonusUseRequests = () => {
  const { t } = useLocalization();
  const [bonusRequests, setBonusRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [approvingBonus, setApprovingBonus] = useState(null);
  const [cancelingBonus, setCancelingBonus] = useState(null);

  // Helper functions for expiration handling
  const isExpiringSoon = (expirationDate) => {
    if (!expirationDate) return false;
    const now = new Date();
    const expiration = new Date(expirationDate);
    const timeDiff = expiration.getTime() - now.getTime();
    const hoursDiff = timeDiff / (1000 * 3600);
    return hoursDiff <= 24 && hoursDiff > 0;
  };

  const isExpired = (expirationDate) => {
    if (!expirationDate) return false;
    const now = new Date();
    const expiration = new Date(expirationDate);
    return expiration < now;
  };

  const formatExpirationTime = (expirationDate) => {
    if (!expirationDate) return t('never_expires');
    
    const now = new Date();
    const expiration = new Date(expirationDate);
    const timeDiff = expiration.getTime() - now.getTime();
    
    if (timeDiff <= 0) {
      return t('expired');
    }
    
    const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));
    const hoursDiff = Math.floor((timeDiff % (1000 * 3600 * 24)) / (1000 * 3600));
    
    if (daysDiff > 0) {
      return `${daysDiff} ${t('days')} ${hoursDiff} ${t('hours')}`;
    } else if (hoursDiff > 0) {
      return `${hoursDiff} ${t('hours')}`;
    } else {
      return t('less_than_hour');
    }
  };

  const getExpirationColor = (expirationDate) => {
    if (!expirationDate) return 'success';
    if (isExpired(expirationDate)) return 'error';
    if (isExpiringSoon(expirationDate)) return 'warning';
    return 'info';
  };

  const getExpirationIcon = (expirationDate) => {
    if (!expirationDate) return '♾️';
    if (isExpired(expirationDate)) return '⏰';
    if (isExpiringSoon(expirationDate)) return '⚠️';
    return '📅';
  };

  useEffect(() => {
    fetchBonusRequests();
  }, []);

  const fetchBonusRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('adminToken');
      
      const response = await axios.get('/users/bonus_usage_requests', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setBonusRequests(response.data);
    } catch (err) {
      console.error('Error fetching bonus usage requests:', err);
      setError(t('failed_to_load_bonus_requests'));
    } finally {
      setLoading(false);
    }
  };

  const handleApproveBonus = async (bonusId) => {
    try {
      setApprovingBonus(bonusId);
      const token = localStorage.getItem('adminToken');
      
      await axios.post(`/users/approve_bonus_usage/${bonusId}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Remove the approved bonus from the list
      setBonusRequests(prev => prev.filter(bonus => bonus.attachment_bonus_id !== bonusId));
      
      // Dispatch event to update counter in sidebar
      window.dispatchEvent(new CustomEvent('bonusRequestUpdated'));
    } catch (err) {
      console.error('Error approving bonus:', err);
      setError(t('failed_to_approve_bonus'));
    } finally {
      setApprovingBonus(null);
    }
  };

  const handleCancelBonus = async (bonusId) => {
    try {
      setCancelingBonus(bonusId);
      const token = localStorage.getItem('adminToken');
      
      await axios.post(`/users/cancel_bonus_usage/${bonusId}`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Remove the cancelled bonus from the list
      setBonusRequests(prev => prev.filter(bonus => bonus.attachment_bonus_id !== bonusId));
      
      // Dispatch event to update counter in sidebar
      window.dispatchEvent(new CustomEvent('bonusRequestUpdated'));
    } catch (err) {
      console.error('Error canceling bonus:', err);
      setError(t('failed_to_cancel_bonus'));
    } finally {
      setCancelingBonus(null);
    }
  };

  const getBonusTypeIcon = (type) => {
    switch (type) {
      case 'discount':
        return <AttachMoney />;
      case 'free_item':
        return <CardGiftcardIcon />;
      case 'points':
        return <AutoAwesomeIcon />;
      default:
        return <CardGiftcardIcon />;
    }
  };

  const getBonusTypeColor = (type) => {
    switch (type) {
      case 'discount':
        return 'primary';
      case 'free_item':
        return 'secondary';
      case 'points':
        return 'success';
      default:
        return 'default';
    }
  };

  const columns = [
    { 
      field: 'user_email', 
      headerName: t('user_email'), 
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar sx={{ 
            width: 24, 
            height: 24, 
            mr: 1, 
            bgcolor: 'secondary.main',
            fontSize: '0.75rem'
          }}>
            <EmailIcon sx={{ fontSize: 16 }} />
          </Avatar>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value}
          </Typography>
        </Box>
      )
    },
    { 
      field: 'calculated_bonus', 
      headerName: t('bonus_amount'), 
      flex: 1,
      minWidth: 60,
      type: 'number',
      renderCell: (params) => {
        const bonusValue = params.value;
        const bonusType = params.row.bonus_type;
        const displayValue = bonusType === 'discount' ? `${bonusValue}%` : 
                           bonusType === 'free_item' ? `${bonusValue}${t('currency')}` : bonusValue;
        
        return (
          <Chip
            icon={<AutoAwesomeIcon />}
            label={displayValue}
            color="success"
            variant="filled"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
      }
    },
    { 
      field: 'bonus_type', 
      headerName: t('bonus_type'), 
      flex: 1,
      minWidth: 220,
      renderCell: (params) => {
        const getLocalizedBonusType = (type) => {
          switch (type) {
            case 'discount':
              return t('bonus_type_discount');
            case 'free_item':
              return t('bonus_type_free_item');
            case 'points':
              return t('bonus_type_points');
            default:
              return type;
          }
        };
        
        return (
          <Chip
            icon={getBonusTypeIcon(params.value)}
            label={getLocalizedBonusType(params.value)}
            color={getBonusTypeColor(params.value)}
            variant="outlined"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
      }
    },
    { 
      field: 'bonus_expiration', 
      headerName: t('expiration_date'), 
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => {
        if (!params.row.bonus_expiration) {
          return params.row.bonus_type === 'points' ? t('never_expires') : 'N/A';
        }
        return formatExpirationTime(params.row.bonus_expiration);
      },
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span style={{ fontSize: '16px' }}>
            {getExpirationIcon(params.row.bonus_expiration)}
          </span>
          <Chip
            icon={<ScheduleIcon />}
            label={params.value}
            color={getExpirationColor(params.row.bonus_expiration)}
            variant="outlined"
            size="small"
            sx={{ 
              fontWeight: 'bold',
              fontSize: '0.75rem'
            }}
          />
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: t('actions'),
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t('approve_bonus_usage')}>
            <IconButton
              color="success"
              onClick={() => handleApproveBonus(params.row.attachment_bonus_id)}
              disabled={approvingBonus === params.row.attachment_bonus_id || cancelingBonus === params.row.attachment_bonus_id}
              sx={{ 
                '&:hover': {
                  bgcolor: 'success.light',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {approvingBonus === params.row.attachment_bonus_id ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CheckCircleIcon />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title={t('cancel_bonus_usage')}>
            <IconButton
              color="error"
              onClick={() => handleCancelBonus(params.row.attachment_bonus_id)}
              disabled={cancelingBonus === params.row.attachment_bonus_id || approvingBonus === params.row.attachment_bonus_id}
              sx={{ 
                '&:hover': {
                  bgcolor: 'error.light',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              {cancelingBonus === params.row.attachment_bonus_id ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <CancelIcon />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const totalRequests = bonusRequests.length;
  const totalBonusValue = bonusRequests.reduce((sum, bonus) => sum + (bonus.calculated_bonus || 0), 0);
  const discountBonuses = bonusRequests.filter(bonus => bonus.bonus_type === 'discount').length;
  const freeItemBonuses = bonusRequests.filter(bonus => bonus.bonus_type === 'free_item').length;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <Avatar sx={{ 
          width: 60, 
          height: 60, 
          mr: 3, 
          bgcolor: 'primary.main',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}>
          <CardGiftcardIcon sx={{ fontSize: 30 }} />
        </Avatar>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            {t('bonus_usage_requests_title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('bonus_usage_requests_subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setError('')}
        >
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      {bonusRequests.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          <Card sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            minWidth: 200,
            flex: 1
          }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Avatar sx={{ 
                width: 40, 
                height: 40, 
                mx: 'auto', 
                mb: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.2)'
              }}>
                <CardGiftcardIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {totalRequests}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {t('pending_requests')}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ 
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white',
            minWidth: 200,
            flex: 1
          }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Avatar sx={{ 
                width: 40, 
                height: 40, 
                mx: 'auto', 
                mb: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.2)'
              }}>
                <AttachMoney sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {discountBonuses}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {t('discount_bonuses')}
              </Typography>
            </CardContent>
          </Card>

          <Card sx={{ 
            background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
            color: 'white',
            minWidth: 200,
            flex: 1
          }}>
            <CardContent sx={{ textAlign: 'center', p: 2 }}>
              <Avatar sx={{ 
                width: 40, 
                height: 40, 
                mx: 'auto', 
                mb: 1.5,
                bgcolor: 'rgba(255, 255, 255, 0.2)'
              }}>
                <CardGiftcardIcon sx={{ fontSize: 20 }} />
              </Avatar>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                {freeItemBonuses}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {t('free_item_bonuses')}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Bonus Requests Data Grid */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress size={60} />
        </Box>
      ) : (
        <Card sx={{ boxShadow: 3, borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar sx={{ 
                width: 50, 
                height: 50, 
                mr: 2, 
                bgcolor: 'primary.main'
              }}>
                <CardGiftcardIcon sx={{ fontSize: 25 }} />
              </Avatar>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {t('bonus_usage_requests_list')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {bonusRequests.length > 0 
                    ? t('pending_requests_count').replace('{count}', bonusRequests.length)
                    : t('no_pending_requests')
                  }
                </Typography>
              </Box>
            </Box>
            
            {bonusRequests.length > 0 ? (
              <Paper sx={{ height: 500, width: '100%', borderRadius: 2, overflow: 'hidden' }}>
                <DataGrid
                  rows={bonusRequests}
                  columns={columns}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25, 50]}
                  getRowId={(row) => `${row.attachment_bonus_id}`}
                  disableSelectionOnClick
                  autoWidth
                  sx={{
                    '& .MuiDataGrid-cell': {
                      borderBottom: '1px solid #e0e0e0',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      fontWeight: 'bold',
                    },
                    '& .MuiDataGrid-row:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '& .MuiDataGrid-columnHeader': {
                      flex: 1,
                    },
                    '& .MuiDataGrid-cell': {
                      flex: 1,
                    },
                  }}
                />
              </Paper>
            ) : (
              <Box sx={{ 
                textAlign: 'center', 
                py: 8,
                color: 'text.secondary'
              }}>
                <CardGiftcardIcon sx={{ fontSize: 80, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t('no_pending_requests_title')}
                </Typography>
                <Typography variant="body1">
                  {t('all_requests_processed')}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default BonusUseRequests; 